import {createTheme} from '@mui/material/styles';
import {SD_BUTTON_RADIUS} from '../constants/ui-tokens';

// ────────────────────────────────────────────────────────────────────────────
// White-label base theme
//
// A "brand" is pure config — adding a new white-label org is a new preset object,
// not a copy of the whole theme. buildTheme(brand) reproduces the exact structure
// the three hand-written themes had; the RED/GREEN/ORANGE presets below capture
// every value (and legacy quirk) that differed between them, so the built themes
// are byte-identical to the originals.
//
// NOTE ON LEGACY QUIRKS: the original three themes had copy-paste drift that is
// preserved verbatim to avoid any visual change — `hasCaption` (only red overrode
// the caption variant), `h3FontWeight` (600 red vs 900 green/orange), a per-brand
// field `fontSize` `!important`, and green's no-space `...!important`. A brand-new
// white-label brand should just copy RED_BRAND's sensible values.

const RED_BRAND = {
  primaryMain: '#fd4958',
  secondaryMain: '#02140A',
  textPrimary: '#ffffff',
  textSecondary: 'red',
  iconPrimary: '#ffffff',
  iconSecondary: '#ffffff',
  checkboxPrimary: '#fd4958',
  loaderPrimary: '#ffffff',
  backgroundPrimary: '#222',
  backgroundHover: '#d32f2f',
  backgroundSecondary: 'linear-gradient(135deg, #d0373b, #6d0407)',
  modalBg: '#9e0001',
  modalText: '#ffffff',
  typoColor: '#ffffff',
  h3FontWeight: 600,
  hasCaption: true,
  bodyGradient: 'linear-gradient(330deg, #ca0000 64%, #9e0001 58%)',
  bodyColor: '#ca0000',
  drawerPaperBg: 'rgba(0, 0, 0, 0.8) !important',
  alertBg: 'rgba(255,255,255,0.5) !important',
  alertColor: '#fff',
  selectRootColor: 'white',
  selectBg: 'rgba(255,255,255,0.5) !important',
  selectIconColor: 'white',
  selectTypoColor: '#fff',
  fclLabelColor: '#ffffff',
  fclFocusColor: '#fff',
  radioColor: '#fff',
  formLabelColor: '#ffffff',
  radioGroupColor: '#ffffff',
  accordionBg: '#fff',
  accordionTypoColor: '#fd4958',
  fieldFill: 'rgba(255,255,255,0.5) !important',
  fieldTextColor: 'white',
  fieldFontSize: '1.2rem',
  fieldFocusFill: 'rgba(255,255,255,0.5) !important'
};

const GREEN_BRAND = {
  primaryMain: '#c3fd49',
  secondaryMain: '#d6e2db',
  textPrimary: '#baea10',
  textSecondary: '#baea10',
  iconPrimary: '#baea10',
  iconSecondary: '#baea10',
  checkboxPrimary: '#c3fd49',
  loaderPrimary: '#baea10',
  backgroundPrimary: '#4caf50',
  backgroundHover: '#388e3c',
  backgroundSecondary: '#388e3c',
  modalBg: '#4caf50',
  modalText: '#baea10',
  typoColor: '#baea10',
  h3FontWeight: 900,
  hasCaption: false,
  bodyGradient: 'linear-gradient(330deg, #4caf50 64%, #388e3c 58%)',
  bodyColor: '#4caf50',
  drawerPaperBg: 'rgba(0, 0, 0, 0.8) !important',
  alertBg: 'rgba(255,255,255,0.5) !important',
  alertColor: '#baea10',
  selectRootColor: '#baea10',
  selectBg: 'rgba(0,255,0,0.3) !important',
  selectIconColor: '#baea10',
  selectTypoColor: '#baea10',
  fclLabelColor: '#baea10',
  fclFocusColor: '#baea10',
  radioColor: '#baea10',
  formLabelColor: '#baea10',
  radioGroupColor: '#baea10',
  accordionBg: '#baea10',
  accordionTypoColor: '#c3fd49',
  fieldFill: 'rgba(0,255,0,0.3) !important',
  fieldTextColor: 'white',
  fieldFontSize: '1.2rem !important',
  fieldFocusFill: 'rgba(0,255,0,0.3)!important'
};

const ORANGE_BRAND = {
  primaryMain: '#f57c00',
  secondaryMain: '#02140A',
  textPrimary: '#ffc78e',
  textSecondary: '#f75606',
  iconPrimary: '#ffc78e',
  iconSecondary: '#f75606',
  checkboxPrimary: '#f57c00',
  loaderPrimary: '#ffc78e',
  backgroundPrimary: '#f57c00',
  backgroundHover: '#ff9800',
  backgroundSecondary: '#ff9800',
  modalBg: '#f57c00',
  modalText: '#ffc78e',
  typoColor: '#ffc78e',
  h3FontWeight: 900,
  hasCaption: false,
  bodyGradient: 'linear-gradient(330deg, #f57c00 64%, #ff9800 58%)',
  bodyColor: '#f57c00',
  drawerPaperBg: 'rgba(235, 149, 50, 1) !important',
  alertBg: 'rgba(242,120,75,1) !important',
  alertColor: '#ffc78e',
  selectRootColor: 'white',
  selectBg: 'rgba(242,120,75,1) !important',
  selectIconColor: 'white',
  selectTypoColor: '#ffc78e',
  fclLabelColor: '#ffc78e',
  fclFocusColor: '#ffc78e',
  radioColor: '#ffc78e',
  formLabelColor: '#ffc78e',
  radioGroupColor: '#ffc78e',
  accordionBg: '#fff',
  accordionTypoColor: '#ffc78e',
  fieldFill: 'rgba(242,120,75,1) !important',
  fieldTextColor: '#ffc78e',
  fieldFontSize: '1.2rem !important',
  fieldFocusFill: 'rgba(242,120,75,1) !important'
};

function buildTypography(b) {
  return {
    fontFamily: 'Poppins, sans-serif',
    h1: {color: b.typoColor},
    h2: {color: b.typoColor},
    h3: {fontSize: '2rem', fontWeight: b.h3FontWeight, color: b.typoColor},
    h4: {fontSize: '1.5rem', fontWeight: 400, color: b.typoColor},
    h5: {fontSize: '1.3rem', fontWeight: 400, color: b.typoColor},
    h6: {fontSize: '1rem', fontWeight: 400, color: b.typoColor},
    body1: {fontSize: '.9rem', fontWeight: 400, color: b.typoColor},
    body2: {fontSize: '0.9rem', fontWeight: 400, color: b.typoColor},
    body3: {fontSize: '0.8rem', fontWeight: 200, color: b.typoColor},
    body4: {fontSize: '2rem', fontWeight: 800, color: b.typoColor},
    subtitle2: {fontSize: '0.6rem', fontWeight: 400, color: b.typoColor},
    button: {
      fontSize: '1rem',
      // fontWeight: 400,
      color: b.typoColor
    },
    // Only red overrode the caption variant historically.
    ...(b.hasCaption ? {caption: {color: b.typoColor}} : {}),
    p: {color: b.typoColor}
  };
}

function buildComponents(b) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: b.bodyGradient,
          backgroundColor: b.bodyColor,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          overflowX: 'hidden'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          // SIAA-611 standardised button rounding onto one token app-wide.
          borderRadius: SD_BUTTON_RADIUS,
          fontWeight: '600',
          width: '100%',
          '@media (max-width: 600px)': {
            // Adjust the max-width according to your requirements
            height: '3.2rem'
          },
          '@media (min-width: 601px)': {
            // Set default height for larger screens
            height: '2.8rem'
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: b.drawerPaperBg,
          backdropFilter: 'blur(5px)',
          border: 'none',
          boxShadow: 'none',
          width: '250px',
          '& .MuiListItemText-primary': {
            fontSize: '1.2rem'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent'
        }
      }
    },
    MuiInputLabel: {
      root: {
        fontSize: '1.2rem'
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          backgroundColor: b.alertBg,
          '& .MuiAlert-message': {
            fontSize: '0.8rem',
            fontWeight: '300',
            color: b.alertColor
          },
          '& .MuiAlert-action': {
            fontSize: '0.8rem',
            fontWeight: '300',
            color: b.alertColor
          },

          '& .MuiAlert-icon': {
            fontSize: '1.2rem',
            fontWeight: '600',
            color: b.alertColor
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.MuiSelect-select': {
            outline: 'none',
            border: 'none',
            textAlign: 'left'
          },
          '&. MuiTypography-root': {
            fontSize: '1.2rem',
            fontWeight: '600',
            color: b.selectTypoColor
          }
        },
        root: {
          color: b.selectRootColor,
          border: 'none',
          borderRadius: '16px',
          background: b.selectBg
        },
        icon: {
          color: b.selectIconColor
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%'
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '1.1rem',
          color: b.fclLabelColor
        },
        '& .Mui focused': {
          color: b.fclFocusColor
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: b.radioColor
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '1.1rem',
          color: b.formLabelColor,
          textAlign: 'left'
        }
      }
    },
    MuiRadioGroup: {
      styleOverrides: {
        root: {
          color: b.radioGroupColor
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: b.accordionBg,
          borderRadius: '12px',
          '& .MuiTypography-root': {
            color: b.accordionTypoColor,
            fontSize: '1.4rem'
          },
          '&:first-of-type': {
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
          width: '100%',
          '& .MuiInputBase-input:not(.text-black)': {
            padding: '10px !important',
            borderRadius: '12px',
            backgroundColor: b.fieldFill,
            color: b.fieldTextColor,
            fontSize: b.fieldFontSize
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none !important'
            }
          },
          '& .MuiInputBase-input:focus': {
            backgroundColor: b.fieldFocusFill
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'white !important',
            fontSize: '1rem !important'
          },
          '& .MuiInputBase-root.Mui-error': {
            border: 'none !important'
          },
          '& .MuiFormHelperText-root.Mui-error': {
            color: '#fff !important'
          },
          '& .MuiInputLabel-root': {
            fontSize: '1.2rem !important'
          }
        }
      }
    }
  };
}

/**
 * Build a base (per-org) theme from a brand preset.
 * @param {object} brand  one of the *_BRAND presets (or a white-label brand object)
 */
export function buildTheme(brand) {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {main: brand.primaryMain},
      error: {main: '#fff'},
      secondary: {main: brand.secondaryMain},
      text: {
        primary: brand.textPrimary,
        secondary: brand.textSecondary // You can define your own custom text colors
      },
      icon: {
        primary: brand.iconPrimary,
        secondary: brand.iconSecondary // You can define your own custom text colors
      },
      checkbox: {primary: brand.checkboxPrimary},
      loader: {primary: brand.loaderPrimary},
      background: {
        primary: brand.backgroundPrimary,
        hover: brand.backgroundHover,
        secondary: brand.backgroundSecondary
      },
      modal: {bgcolor: brand.modalBg, textcolor: brand.modalText}
    },
    typography: buildTypography(brand),
    components: buildComponents(brand)
  });
}

// Named brand presets — the registry a white-label lookup can grow.
export const brands = {red: RED_BRAND, green: GREEN_BRAND, orange: ORANGE_BRAND};

// Backward-compatible exports: 11 files import the default theme directly at
// module scope, so the default export must stay a fully-built red theme object.
const theme = buildTheme(RED_BRAND);
export default theme;
export const greenTheme = buildTheme(GREEN_BRAND);
export const orangeTheme = buildTheme(ORANGE_BRAND);

// Org themeType → brand / built theme. Keeps App.js data-driven: a new white-label
// brand is a preset + one registry entry, not scattered switch/ternary edits.
const BRAND_BY_TYPE = {1: RED_BRAND, 2: GREEN_BRAND, 3: ORANGE_BRAND};
const THEME_BY_TYPE = {1: theme, 2: greenTheme, 3: orangeTheme};
export function brandForType(themeType) {
  return BRAND_BY_TYPE[Number(themeType)] || RED_BRAND;
}
export function themeForType(themeType) {
  return THEME_BY_TYPE[Number(themeType)] || theme;
}

// ────────────────────────────────────────────────────────────────────────────
// Admin light-surface theme
//
// The base themes above are authored for the dark login/auth backgrounds: white
// text, translucent-white borderless inputs, white radios. Admin pages render on
// white cards, so we layer a LIGHT-SURFACE theme over the current org theme and
// apply it (via a nested <ThemeProvider>) to the admin content region only — the
// dark Sidebar/Header stay on the base theme.
//
// createTheme(base, overrides) DEEP-MERGES components.*.styleOverrides, so each
// override below redefines the EXACT selector keys the base sets (matching its
// `!important`) — otherwise the base's white rules survive the merge.
const ADMIN_TEXT = '#02140A'; // near-black body/input text
const ADMIN_LABEL = '#6b7280'; // muted field-label gray
const ADMIN_BORDER = '#000000'; // 1px input border (matches AdminTextInput/StyledSelectInput)
const ADMIN_FOCUS = '#d32f2f'; // red focus/border (matches existing admin aesthetic)

/**
 * Layer light-surface admin overrides onto a base (per-org) theme.
 * @param {import('@mui/material/styles').Theme} base  a built base theme (red/green/orange)
 */
export function buildAdminTheme(base = theme) {
  return createTheme(base, {
    palette: {
      text: {primary: ADMIN_TEXT, secondary: ADMIN_LABEL},
      // Modal.jsx falls back to palette.modal.* when a caller omits color props
      // (e.g. Draft Day's remove-segment modal) — must be light here.
      modal: {bgcolor: '#ffffff', textcolor: ADMIN_TEXT}
    },
    // NOTE: intentionally NOT flipping the per-variant typography colors to dark.
    // Some admin surfaces are colored (e.g. the red Reading-Plan editor card) and
    // rely on the base theme's white text; a global dark flip would make them
    // low-contrast. Admin pages set explicit text colors on their own headings
    // (Draft Day does), and readable FORM fields come from the component overrides
    // below — which is the actual problem we're solving.
    components: {
      // Buttons size to their own label instead of being squeezed into it (SIAA-642).
      //
      // The base MuiButton override (buildComponents, ~line 173) sets `width: '100%'` and a HARD
      // `height`. On mobile/journey screens that is the intended full-bleed CTA. In an admin
      // modal it is the bug Jaime reported: a 400px dialog gives two buttons ~150px each (three
      // buttons ~93px), MUI's own `minWidth: 64` lets them shrink below the text's min-content
      // width, nothing sets `whiteSpace`, so the label wraps — and a second line does not fit a
      // locked 44.8px box. Measured before this fix: "Leave Without Saving" rendered
      // scrollHeight 63 inside clientHeight 43, i.e. 20px of white text outside the painted
      // button, on a white dialog. Invisible.
      //
      // Scoped to buildAdminTheme deliberately. AdminStructure applies this theme and React
      // context reaches portaled dialogs, so admin modals are covered while the mobile and
      // journey full-bleed CTAs — which several callers reinforce with an explicit height — are
      // left exactly as they are.
      //
      // NO `whiteSpace: 'nowrap'` on purpose: that trades vertical overflow for horizontal, and
      // the dialog Paper's `overflow: 'hidden'` clips that just as happily. Letting a long
      // translation wrap and the BUTTON GROW is the one pattern in this codebase that already
      // survives Spanish and Odia (AutoPromote's toggleSx — padding-sized, no fixed height).
      // That matters: short labels expand far past the ~35% the design handoff assumes —
      // `Save` 4→12 chars in Odia, `Email` 5→19 in Spanish.
      MuiButton: {
        styleOverrides: {
          root: {
            width: 'auto',
            // minHeight, never height — keeps the touch target while allowing a second line.
            height: 'auto',
            minHeight: '2.8rem',
            lineHeight: 1.25,
            // Stop flex from squeezing a button below the width its own text needs.
            flexShrink: 0,
            // Breathing room on all four sides. CustomButton only sets padding inside its
            // `height &&` branch, so a button with no explicit height fell back to MUI's
            // default 6px vertical — which is what makes a wrapped label look jammed against
            // the edge even once it fits. Vertical padding + `height: auto` means a second
            // line pushes the button taller instead of crowding the first.
            padding: '0.625rem 1.25rem',
            // A one-word label ("Save", "Finish") would otherwise render as a tiny nub next to
            // a longer sibling. A floor keeps a row of short verbs visually even; it is under
            // a third of the 400px dialog so three of them still fit on one line.
            minWidth: '6rem',
            '@media (max-width: 600px)': {
              height: 'auto',
              minHeight: '3.2rem'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            // Flip the base's white-on-translucent input to dark-on-white.
            '& .MuiInputBase-input:not(.text-black)': {
              backgroundColor: '#ffffff',
              color: ADMIN_TEXT
            },
            // Base removes the outline with `border:none !important`; restore it.
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: `1px solid ${ADMIN_BORDER} !important`
              }
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              '& fieldset': {
                borderColor: `${ADMIN_FOCUS} !important`
              }
            },
            '& .MuiInputBase-input:focus': {
              backgroundColor: '#ffffff !important'
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#888888 !important'
            },
            '& .MuiFormHelperText-root.Mui-error': {
              color: `${ADMIN_FOCUS} !important`
            }
          }
        }
      },
      // Base has no MuiOutlinedInput/MuiInputBase overrides — add dark text +
      // WebkitTextFillColor so text stays readable in blur/disabled states (the
      // white-on-blur bug that DraftDay's DARK_FIELD_SX hacked around).
      // MuiOutlinedInput underlies BOTH the outlined TextField and the standalone
      // Select, so it owns the single 1px border + 8px radius for every admin field.
      MuiOutlinedInput: {
        styleOverrides: {
          input: {color: ADMIN_TEXT, WebkitTextFillColor: ADMIN_TEXT},
          notchedOutline: {borderColor: ADMIN_BORDER},
          root: {
            borderRadius: '8px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: ADMIN_FOCUS}
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          input: {color: ADMIN_TEXT, WebkitTextFillColor: ADMIN_TEXT}
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&. MuiTypography-root': {color: ADMIN_TEXT}
          },
          // No `border` here — the border comes from MuiOutlinedInput's notchedOutline;
          // adding one on the root would double it up.
          root: {
            color: ADMIN_TEXT,
            background: '#ffffff !important'
          },
          icon: {color: ADMIN_TEXT} // visible dark dropdown arrow (base was white)
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {color: ADMIN_FOCUS, '&.Mui-checked': {color: ADMIN_FOCUS}}
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {color: ADMIN_TEXT, '&.Mui-checked': {color: base.palette.primary.main}}
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {color: ADMIN_LABEL, '&.Mui-focused': {color: ADMIN_FOCUS}}
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {color: ADMIN_TEXT}
        }
      },
      MuiRadioGroup: {
        styleOverrides: {
          root: {color: ADMIN_TEXT}
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {color: ADMIN_LABEL}
        }
      }
    }
  });
}

// Precomputed admin themes by org themeType (1=red default, 2=green, 3=orange),
// so AdminStructure can pick one without rebuilding on every render.
export const adminThemes = {
  1: buildAdminTheme(theme),
  2: buildAdminTheme(greenTheme),
  3: buildAdminTheme(orangeTheme)
};

/** Pick the admin theme for an org themeType, defaulting to red. */
export function adminThemeForType(themeType) {
  return adminThemes[Number(themeType)] || adminThemes[1];
}
